import React, { useEffect, useState } from 'react';
import { Alert, IconButton } from '@mui/material';

import { ILoad, ILoadMaterial } from '../../types/load';
import { IManifest, IManifestMaterialAssignment, IManifestSaveRequest } from '../../types/manifest';
import { ISelectOption } from '../../types/list';
import { ITransporterLight } from '../../types/transporter';

import { ManifestService } from '../../services/manifestService';
import { ErrorProvider } from '../../classes/errorProvider'

import Spinner from '../spinner';
import { Icons } from '../icons';
import WorkingModal, { IWorkingModalProps } from '../workingModal';
import MaterialManifestManager from '../shared/materialManifestManager';
import Json from '../json';

import '../../styles/loadManifestsForm.scss';

export interface ILoadManifestsFormProps {
	load?: ILoad;
	isLoadingLoad: boolean;
	materials: ILoadMaterial[];
	manifests: IManifest[];
	isLoadingManifests: boolean;
	manifestVendors: ISelectOption[];
	isLoadingManifestVendors: boolean;
	secondaryTransporters: ITransporterLight[];
	isLoadingSecondaryTransporters: boolean;
	manifestsUpdated: (manifests: IManifest[]) => void;
	materialsUpdated: (materials: ILoadMaterial[]) => void;
}

const LoadManifestsForm: React.FunctionComponent<ILoadManifestsFormProps> = ({ load, isLoadingLoad, materials, manifests, isLoadingManifests, manifestVendors, isLoadingManifestVendors, secondaryTransporters, isLoadingSecondaryTransporters, manifestsUpdated, materialsUpdated }) => {
	const [manifestBeingEditedId, setManifestBeingEditedId] = useState<number | undefined>(undefined);

	//Fields
	const [showAddForm, setShowAddForm] = useState(false);
	const [isSavingManifestData, setIsSavingManifestData] = useState(false);
	const [isDeletingManifest, setIsDeletingManifest] = useState(false);
	const [isDataReady, setIsDataReady] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	//#region useEffects
	useEffect(() => {
		if (!isLoadingLoad && !isLoadingManifests && !isLoadingManifestVendors && !isLoadingSecondaryTransporters) {
			setIsDataReady(true);
		}
	}, [isLoadingLoad, isLoadingManifests, isLoadingManifestVendors, isLoadingSecondaryTransporters])
	//#endregion useEffects

	//#region Event handlers
	// Only one manifest can be edited at a time. Hide the other manifest manage components when one is being edited
	const handleEditRequested = (manifestId: number) => {
		if (!manifestBeingEditedId && manifestId) {
			setManifestBeingEditedId(manifestId);
		}
	}

	const handleCancelRequested = (manifestId: number) => {
		if (manifestBeingEditedId === manifestId) {
			setManifestBeingEditedId(undefined);
		}
	}

	const handleDeleteRequested = (manifest: IManifest) => {
		setIsDeletingManifest(true);
		setErrorMessage(undefined);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="content">
							Deleting Manifest <strong>{manifest.number}</strong>...
						</div>
					</div>
			}
		});

		ManifestService.deleteManifest(manifest.id!)
			.then((response) => {
				console.log('ManifestService.deleteManifest: SUCCESS', response);
				const updatedManifests = manifests.filter(m => m.id !== manifest.id);
				manifestsUpdated(updatedManifests);
				materialsUpdated(materials.map(m => m.manifestId === manifest.id ? { ...m, manifestId: undefined } : m));
			})
			.catch((error) => {
				console.log('ManifestService.deleteManifest: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			}).finally(() => {
				setIsDeletingManifest(false);
				setWorkingModalParams(undefined);
			});
	}

	const handleManifestUpdated = (manifest: IManifest, assignments: IManifestMaterialAssignment[]) => {
		if (manifest.id == -1) {
			addManifest(manifest, assignments);
		}
		else {
			updateManifest(manifest, assignments);
		}
	}
	//#endregion Event handlers

	//#region Action methods
	const addManifest = (manifest: IManifest, assignments: IManifestMaterialAssignment[]) => {
		setIsSavingManifestData(true);
		setErrorMessage(undefined);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="content">
							Creating Manifest <strong>{manifest.number}</strong>...
						</div>
					</div>
			}
		});

		const manifestCopy = JSON.parse(JSON.stringify(manifest)) as IManifest;
		// Change -1 IDs to null. -1 is a null placeholder for non-nullables
		manifestCopy.id = manifest.id === -1 ? null : manifest.id;
		manifestCopy.manifestVendorId = manifest.manifestVendorId === -1 ? null : manifest.manifestVendorId;
		manifestCopy.secondaryTransporterId = manifest.secondaryTransporterId === -1 ? null : manifest.secondaryTransporterId;

		const request = { manifest: manifestCopy, assignments, type: "load" } as IManifestSaveRequest;
		ManifestService.addManifest(request)
			.then((response) => {
				console.log('ManifestService.addManifest: SUCCESS', response);
				const updatedManifest = ManifestService.mapManifest(response.data);
				manifestsUpdated([...manifests, updatedManifest]);
				materialsUpdated(applyManifestAssignmentsToMaterials(updatedManifest, assignments, materials));
				setShowAddForm(false);
			})
			.catch((error) => {
				console.log('ManifestService.addManifest: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			}).finally(() => {
				setIsSavingManifestData(false);
				setWorkingModalParams(undefined);
			});
	}

	const updateManifest = (manifest: IManifest, assignments: IManifestMaterialAssignment[]) => {
		setIsSavingManifestData(true);
		setErrorMessage(undefined);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="content">
							Saving changes to Manifest <strong>{manifest.number}</strong>...
						</div>
					</div>
			}
		});

		const request = { manifest, assignments, type: "load" } as IManifestSaveRequest;
		ManifestService.updateManifest(request)
			.then((response) => {
				console.log('ManifestService.updateManifest: SUCCESS', response);
				const updatedManifest = ManifestService.mapManifest(response.data);
				let updatedManifests = JSON.parse(JSON.stringify(manifests));
				const staleindex = updatedManifests.findIndex(m => m.id === updatedManifest.id);
				if (staleindex > -1) {
					updatedManifests[staleindex] = updatedManifest;
				}
				manifestsUpdated(updatedManifests);
				materialsUpdated(applyManifestAssignmentsToMaterials(updatedManifest, assignments, materials));
				setManifestBeingEditedId(undefined);
			})
			.catch((error) => {
				console.log('ManifestService.updateManifest: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			}).finally(() => {
				setIsSavingManifestData(false);
				setWorkingModalParams(undefined);
			});
	}

	const applyManifestAssignmentsToMaterials = (manifest: IManifest, assignments: IManifestMaterialAssignment[], materials: ILoadMaterial[]) => {
		const updatedMaterials = JSON.parse(JSON.stringify(materials)) as ILoadMaterial[];
		for (let assignment of assignments) {
			const material = updatedMaterials.find(m => m.id === assignment.materialId);
			if (material) {
				// Use the ID from the returned manifest in case this was a new manifest and its original ID was -1
				material.manifestId = assignment.manifestId == -1 ? manifest.id : assignment.manifestId;
			}
			else {
				console.error(`Could not find load material with id ${assignment.materialId} to apply manifest assignment.`)
			}
		}
		return updatedMaterials;
	}
	//#endregion Action methods

	return (
		<section className="hwi-load-manifests-form">
			<Spinner dependency={!isDataReady} text='Loading manifests...'>
				{errorMessage &&
					<Alert
						className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				{!manifestBeingEditedId && load?.status?.toLowerCase() == "loading" &&
					<div className='add-manifest'>
						<div className="load-manifest-header">
							<div className="form-title">Add Manifest</div>
							<span onClick={() => setShowAddForm(!showAddForm)}>
								{showAddForm &&
									<Icons.Up fill="#272f50" height="32px"></Icons.Up>
								}
								{!showAddForm &&
									<Icons.Down fill="#272f50" height="32px"></Icons.Down>
								}
							</span>
						</div>
						{showAddForm &&
							<MaterialManifestManager
								materials={materials}
								lading={load}
								manifestVendors={manifestVendors}
								secondaryTransporters={secondaryTransporters}
								manifestUpdated={handleManifestUpdated}
							></MaterialManifestManager>
						}
					</div>
				}
				{!showAddForm &&
					<div className="edit-manifests">
						{manifests?.length > 0 ? (
							(manifests.filter(m => (!manifestBeingEditedId || manifestBeingEditedId === m.id)).map(m => (
								<MaterialManifestManager
									key={m.id}
									manifest={m}
									lading={load}
									materials={materials}
									manifestVendors={manifestVendors}
									secondaryTransporters={secondaryTransporters}
									manifestIdBeingEdited={manifestBeingEditedId}
									manifestUpdated={handleManifestUpdated}
									deleteRequested={handleDeleteRequested}
									editRequested={handleEditRequested}
									cancelRequested={handleCancelRequested}
								></MaterialManifestManager>
							)))
						) : (
							<div className="no-manifests">This load has no manifests.{load?.status == 'Loading' && <> You can add one using the form above.</>}</div>
						)}
					</div>
				}
				{/* <Json data={manifestEditingId}></Json> */}
			</Spinner>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</section>
	)
}

export default LoadManifestsForm;